import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ADMIN_FEEDBACK_OWNER_ID } from '../threads/types';
import type { Thread, ThreadStatus, ThreadObjectType, AppFeedbackKind } from '../threads/types';
import type { ThreadApi } from '../threads/threadApi';
import { ThreadStatusBadge } from '../threads/ThreadStatusBadge';
import { ThreadConversation } from '../threads/ThreadConversation';
import { AppFeedbackForm } from './AppFeedbackForm';
import { Button, formatDate } from '../components';
import {
    getFeedbackKind,
    getSubmitterName,
    getSubmitterRole,
    getSubmitterEmail,
    getAppContext,
    getUpdatedAt,
    isUnreadByCreator,
} from './appFeedbackHelpers';
import { FEEDBACK_UNREAD_QUERY_KEY } from './useFeedbackReplyMessages';
import './AppFeedbackDetail.css';

// Must match the backend gate (editWindow.ts).
const EDIT_WINDOW_MS = 24 * 60 * 60 * 1000;
const LOCKED_STATUSES: ThreadStatus[] = ['resolved', 'wont_fix', 'closed'];

export interface AppFeedbackAdminMode {
    updateStatus: (status: ThreadStatus) => Promise<Thread>;
    /** Save edits to kind / title / description / status / linked object together (single explicit save). */
    saveFeedback?: (changes: { feedbackKind?: AppFeedbackKind; title?: string; description?: string; status?: ThreadStatus; objectType?: ThreadObjectType; objectId?: string }) => Promise<Thread>;
    /** `showAsPopup`: also show this reply to the submitter as an in-app pop-up. */
    addMessage: (message: string, opts?: { showAsPopup?: boolean }) => Promise<Thread>;
    /** Edit an admin-authored message via the /console route. */
    editMessage?: (messageId: string, message: string) => Promise<Thread>;
    reassign?: (email: string) => Promise<Thread>;
    renderDetailExtra?: (thread: Thread) => React.ReactNode;
}

interface AppFeedbackDetailProps {
    thread: Thread;
    api: ThreadApi;
    onUpdated: (updated: Thread) => void;
    onBack: () => void;
    onNavigateObject?: (objectType: string, objectId: string) => void;
    /** Whether this object type can actually be opened. Omitted ⇒ assume yes.
     *  Without it a parent that has no route for a type still renders a link
     *  whose click silently does nothing. */
    canNavigateObject?: (objectType: string) => boolean;
    /** Viewer's userId — enables author/sender edit affordances. */
    currentUserId?: string;
    adminMode?: AppFeedbackAdminMode;
    /** Read-only viewing (e.g. a teacher looking at a student's feedback): shows
     *  the full conversation incl. admin replies but no reply box, and never
     *  marks the thread seen (the viewer isn't a participant). */
    readOnly?: boolean;
}

const OBJECT_TYPE_LABELS: Record<string, string> = {
    submission: 'Submission',
    question: 'Question',
    hint: 'Hint',
};

const KIND_LABELS: Record<AppFeedbackKind, string> = {
    bug: 'Bug',
    suggestion: 'Suggestion',
    flag: 'Flag',
    survey: 'Survey',
};

const STATUS_OPTIONS: ThreadStatus[] = ['open', 'in_progress', 'resolved', 'wont_fix', 'closed'];
const STATUS_LABELS: Record<ThreadStatus, string> = {
    open: 'Open',
    in_progress: 'In Progress',
    replied: 'Replied',
    resolved: 'Resolved',
    wont_fix: "Won't Fix",
    closed: 'Closed',
};

export function AppFeedbackDetail({ thread, api, onUpdated, onBack, onNavigateObject, canNavigateObject, currentUserId, adminMode, readOnly }: AppFeedbackDetailProps) {
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const queryClient = useQueryClient();
    // Admin reply → also pop it up for the submitter (What's New shell). On by
    // default: a reply the submitter never sees is the problem this solves.
    // Hidden on the admin's own feedback — there is no one else to show it to.
    const [showAsPopup, setShowAsPopup] = useState(true);
    const canPopup = !!adminMode && thread.createdBy !== currentUserId;

    const handleSendMessage = async (message: string) => {
        if (adminMode) {
            const updated = await adminMode.addMessage(message, { showAsPopup: canPopup && showAsPopup });
            onUpdated(updated);
        } else {
            const updated = await api.addMessage(thread.id, ADMIN_FEEDBACK_OWNER_ID, message);
            onUpdated(updated);
        }
    };

    const handleEditMessage = async (messageId: string, message: string) => {
        // Admin-authored replies go via /console (cross-partition, needs userId);
        // user/teacher messages use the partition-keyed user endpoint. We trust
        // ThreadConversation to only surface the Edit affordance on messages the viewer
        // authored, so role-based dispatch matches the viewer's role.
        const author = thread.messages.find((m) => m.id === messageId);
        const updated = author?.senderRole === 'admin' && adminMode?.editMessage
            ? await adminMode.editMessage(messageId, message)
            : await api.editMessage(thread.id, messageId, ADMIN_FEEDBACK_OWNER_ID, message);
        onUpdated(updated);
    };

    // Mark as seen when viewing (creator/user mode only — never for a read-only
    // observer, who isn't the creator and can't PATCH someone else's thread).
    // An effect, once per thread version: the thread prop isn't replaced after
    // the PATCH, so a render-time call re-fired on every re-render — and the
    // badge refresh below causes re-renders.
    const shouldMarkSeen = !adminMode && !readOnly && isUnreadByCreator(thread);
    const updatedAt = getUpdatedAt(thread);
    useEffect(() => {
        if (!shouldMarkSeen) return;
        // The Feedback badge isn't polled, so drop it here once the read lands.
        api.update(thread.id, ADMIN_FEEDBACK_OWNER_ID, { markSeen: true })
            .then(() => queryClient.invalidateQueries({ queryKey: FEEDBACK_UNREAD_QUERY_KEY }))
            .catch(() => {});
    }, [shouldMarkSeen, thread.id, updatedAt, api, queryClient]);

    const canReply = adminMode ? true : readOnly ? false : !LOCKED_STATUSES.includes(thread.status);

    // Only the original author (via the user flow) can edit the feedback body.
    const statusLocked = LOCKED_STATUSES.includes(thread.status);
    const createdMs = Date.parse(thread.createdAt);
    const withinWindow = Number.isFinite(createdMs) && Date.now() - createdMs < EDIT_WINDOW_MS;
    const canEditDescription =
        !adminMode
        && !!currentUserId
        && currentUserId === thread.createdBy
        && !statusLocked
        && withinWindow;

    if (isEditingDescription) {
        return (
            <div className="feedback-detail">
                <button className="feedback-detail-back" onClick={() => setIsEditingDescription(false)}>
                    &larr; Cancel edit
                </button>
                <h2 className="feedback-detail-edit-heading">Edit feedback</h2>
                <AppFeedbackForm
                    api={api}
                    editing={thread}
                    onSubmitted={(updated) => {
                        setIsEditingDescription(false);
                        if (updated) onUpdated(updated);
                    }}
                    onCancel={() => setIsEditingDescription(false)}
                />
            </div>
        );
    }

    const kind = getFeedbackKind(thread);
    const appContext = getAppContext(thread);

    return (
        <div className="feedback-detail">
            <button className="feedback-detail-back" onClick={onBack}>
                &larr; Back
            </button>

            {adminMode ? (
                <AdminFeedbackEditForm
                    thread={thread}
                    saveFeedback={adminMode.saveFeedback}
                    updateStatus={adminMode.updateStatus}
                    onReassign={adminMode.reassign}
                    onUpdated={onUpdated}
                />
            ) : (
                <div className="feedback-detail-header">
                    <div>
                        <div className="feedback-detail-title-row">
                            {kind && (
                                <span className={`feedback-type-label feedback-type-${kind}`}>
                                    {KIND_LABELS[kind] ?? kind}
                                </span>
                            )}
                            <h2>{thread.title}</h2>
                        </div>
                    </div>
                    <ThreadStatusBadge status={thread.status} />
                </div>
            )}

            {thread.objectType && thread.objectId && (
                <div className="feedback-linked-object">
                    <span className="feedback-linked-object-label">
                        {OBJECT_TYPE_LABELS[thread.objectType] || thread.objectType}
                    </span>
                    {onNavigateObject && (canNavigateObject?.(thread.objectType) ?? true) ? (
                        <button
                            className="feedback-linked-object-link"
                            onClick={() => onNavigateObject(thread.objectType!, thread.objectId!)}
                        >
                            Go to {(OBJECT_TYPE_LABELS[thread.objectType] || thread.objectType).toLowerCase()} &rarr;
                        </button>
                    ) : (
                        <span className="feedback-linked-object-id">{thread.objectId.slice(0, 12)}...</span>
                    )}
                </div>
            )}

            {adminMode?.saveFeedback && <LinkedObjectEditor thread={thread} saveFeedback={adminMode.saveFeedback} onUpdated={onUpdated} />}

            {adminMode?.renderDetailExtra?.(thread)}

            {appContext && adminMode && (
                <div className="feedback-context">
                    Page: {appContext.url}
                </div>
            )}

            <ThreadConversation
                description={thread.description}
                creatorName={getSubmitterName(thread)}
                creatorRole={getSubmitterRole(thread)}
                createdAt={thread.createdAt}
                descriptionEditedAt={thread.editedAt}
                screenshotUrls={thread.screenshotUrls}
                messages={thread.messages}
                onSendMessage={handleSendMessage}
                canReply={canReply}
                onEditDescription={canEditDescription ? () => setIsEditingDescription(true) : undefined}
                currentUserId={currentUserId}
                status={thread.status}
                onEditMessage={handleEditMessage}
                markPopupMessages={!!adminMode}
                replyExtraActions={canPopup ? (
                    <label className="feedback-popup-toggle">
                        <input
                            type="checkbox"
                            checked={showAsPopup}
                            onChange={(e) => setShowAsPopup(e.target.checked)}
                        />
                        Show to {getSubmitterName(thread) || 'the submitter'} as a pop-up
                    </label>
                ) : undefined}
            />
        </div>
    );
}

// ── Admin edit form — explicit Save button (no auto-save) ────────────

function AdminFeedbackEditForm({ thread, saveFeedback, updateStatus, onReassign, onUpdated }: {
    thread: Thread;
    saveFeedback?: (changes: { feedbackKind?: AppFeedbackKind; title?: string; description?: string; status?: ThreadStatus }) => Promise<Thread>;
    updateStatus: (status: ThreadStatus) => Promise<Thread>;
    onReassign?: (email: string) => Promise<Thread>;
    onUpdated: (updated: Thread) => void;
}) {
    const initialKind = getFeedbackKind(thread) ?? 'suggestion';
    const [kind, setKind] = useState<AppFeedbackKind>(initialKind);
    const [title, setTitle] = useState(thread.title);
    const [description, setDescription] = useState(thread.description);
    const [status, setStatus] = useState<ThreadStatus>(thread.status);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const updatedAt = getUpdatedAt(thread);

    // Re-sync local draft with thread prop if it changes under us (e.g. after a successful save upstream)
    const syncKey = `${thread.id}|${updatedAt}`;
    const lastSyncRef = useRef(syncKey);
    if (lastSyncRef.current !== syncKey) {
        lastSyncRef.current = syncKey;
        const nextKind = getFeedbackKind(thread) ?? 'suggestion';
        if (kind !== nextKind) setKind(nextKind);
        if (title !== thread.title) setTitle(thread.title);
        if (description !== thread.description) setDescription(thread.description);
        if (status !== thread.status) setStatus(thread.status);
    }

    const dirty =
        kind !== initialKind
        || title !== thread.title
        || description !== thread.description
        || status !== thread.status;

    const handleSave = async () => {
        if (!dirty || saving) return;
        setSaving(true);
        setSaveError(null);
        try {
            const changes: { feedbackKind?: AppFeedbackKind; title?: string; description?: string; status?: ThreadStatus } = {};
            if (kind !== initialKind) changes.feedbackKind = kind;
            if (title !== thread.title) changes.title = title;
            if (description !== thread.description) changes.description = description;
            if (status !== thread.status) changes.status = status;

            let updated: Thread;
            if (saveFeedback) {
                updated = await saveFeedback(changes);
            } else {
                // Fallback: only status can be updated via the legacy single-field callback.
                if (changes.status) updated = await updateStatus(changes.status);
                else throw new Error('Save callback not available');
            }
            onUpdated(updated);
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="feedback-admin-edit">
            <div className="feedback-admin-edit-row">
                <label className="feedback-admin-edit-label">From</label>
                <div className="feedback-admin-edit-from">
                    <OpenedByField thread={thread} onReassign={onReassign} onUpdated={onUpdated} />
                </div>
            </div>
            <div className="feedback-admin-edit-row feedback-admin-edit-row--split">
                <div>
                    <label className="feedback-admin-edit-label">Opened On</label>
                    <div className="feedback-admin-edit-readonly">{formatDate(thread.createdAt)}</div>
                </div>
                <div>
                    <label className="feedback-admin-edit-label">Updated On</label>
                    <div className="feedback-admin-edit-readonly">{formatDate(updatedAt)}</div>
                </div>
            </div>
            <div className="feedback-admin-edit-row feedback-admin-edit-row--split">
                <div>
                    <label className="feedback-admin-edit-label" htmlFor="admin-edit-type">Issue Type</label>
                    <select
                        id="admin-edit-type"
                        className="feedback-admin-edit-input"
                        value={kind}
                        onChange={(e) => setKind(e.target.value as AppFeedbackKind)}
                    >
                        <option value="bug">Bug</option>
                        <option value="suggestion">Suggestion</option>
                        <option value="flag">Flag</option>
                    </select>
                </div>
                <div>
                    <label className="feedback-admin-edit-label" htmlFor="admin-edit-status">Status</label>
                    <select
                        id="admin-edit-status"
                        className="feedback-admin-edit-input"
                        value={status}
                        onChange={(e) => setStatus(e.target.value as ThreadStatus)}
                    >
                        {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="feedback-admin-edit-row">
                <label className="feedback-admin-edit-label" htmlFor="admin-edit-title">Title</label>
                <input
                    id="admin-edit-title"
                    type="text"
                    className="feedback-admin-edit-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={200}
                />
            </div>
            <div className="feedback-admin-edit-row">
                <label className="feedback-admin-edit-label" htmlFor="admin-edit-description">Main Issue</label>
                <textarea
                    id="admin-edit-description"
                    className="feedback-admin-edit-input feedback-admin-edit-textarea"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={5}
                    maxLength={10000}
                />
            </div>
            <div className="feedback-admin-edit-actions">
                {saveError && <span className="feedback-admin-edit-error">{saveError}</span>}
                <Button onClick={handleSave} disabled={!dirty || saving}>
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </div>
        </div>
    );
}

// ── Opened By field (admin only) ─────────────────────────────────────

function OpenedByField({ thread, onReassign, onUpdated }: {
    thread: Thread;
    onReassign?: (email: string) => Promise<Thread>;
    onUpdated: (updated: Thread) => void;
}) {
    const [editing, setEditing] = useState(false);
    const [email, setEmail] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const handleSave = async () => {
        if (!email.trim() || !onReassign) return;
        setSaving(true);
        setError('');
        try {
            const updated = await onReassign(email.trim());
            onUpdated(updated);
            setEditing(false);
            setEmail('');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'User not found');
        } finally {
            setSaving(false);
        }
    };

    if (editing) {
        return (
            <span className="feedback-opened-by-edit">
                <span className="feedback-opened-by-label">Opened by:</span>
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="user@email.com"
                    className="feedback-opened-by-input"
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
                    disabled={saving}
                />
                <button className="feedback-opened-by-btn" onClick={handleSave} disabled={saving || !email.trim()}>
                    {saving ? '...' : 'Save'}
                </button>
                <button className="feedback-opened-by-btn" onClick={() => { setEditing(false); setError(''); }}>
                    Cancel
                </button>
                {error && <span className="feedback-opened-by-error">{error}</span>}
            </span>
        );
    }

    const submitterName = getSubmitterName(thread);
    const submitterRole = getSubmitterRole(thread);
    const submitterEmail = getSubmitterEmail(thread);

    return (
        <span>
            <span>{submitterName} ({submitterRole})</span>
            {submitterEmail && <span> &middot; {submitterEmail}</span>}
            {onReassign && (
                <button
                    className="feedback-opened-by-change"
                    onClick={() => setEditing(true)}
                    title="Change who this feedback is attributed to"
                >
                    Change
                </button>
            )}
        </span>
    );
}

// ── Linked Object Editor (admin only) ───────────────────────────────

const OBJECT_TYPES = ['submission', 'question', 'hint'] as const;

function LinkedObjectEditor({ thread, saveFeedback, onUpdated }: {
    thread: Thread;
    saveFeedback: (changes: { objectType?: ThreadObjectType; objectId?: string }) => Promise<Thread>;
    onUpdated: (updated: Thread) => void;
}) {
    const [editing, setEditing] = useState(false);
    const [objectType, setObjectType] = useState<ThreadObjectType | ''>(thread.objectType || '');
    const [objectId, setObjectId] = useState(thread.objectId || '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const handleSave = async () => {
        if (!objectType || !objectId.trim()) return;
        setSaving(true);
        setError('');
        try {
            const updated = await saveFeedback({ objectType, objectId: objectId.trim() });
            onUpdated(updated);
            setEditing(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    if (!editing && !thread.objectType) {
        return (
            <button className="feedback-link-object-btn" onClick={() => setEditing(true)}>
                + Link Object
            </button>
        );
    }

    if (!editing) return null;

    return (
        <div className="feedback-link-object-editor">
            <label className="feedback-admin-edit-label">Link Object</label>
            <div className="feedback-link-object-row">
                <select
                    className="feedback-admin-edit-input"
                    value={objectType}
                    onChange={e => setObjectType(e.target.value as ThreadObjectType | '')}
                >
                    <option value="">— type —</option>
                    {OBJECT_TYPES.map(t => (
                        <option key={t} value={t}>{OBJECT_TYPE_LABELS[t]}</option>
                    ))}
                </select>
                <input
                    type="text"
                    className="feedback-admin-edit-input"
                    placeholder="Object ID"
                    value={objectId}
                    onChange={e => setObjectId(e.target.value)}
                />
                <Button onClick={handleSave} disabled={!objectType || !objectId.trim() || saving}>
                    {saving ? 'Saving…' : 'Save'}
                </Button>
                <button className="feedback-opened-by-btn" onClick={() => { setEditing(false); setError(''); }}>
                    Cancel
                </button>
            </div>
            {error && <span className="feedback-opened-by-error">{error}</span>}
        </div>
    );
}
